import { useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import toast, { Toaster, useToasterStore } from 'react-hot-toast';
import { IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { AccessibleDivButton } from '@/components/AccessibleDivButton';
import styles from './Snackbar.style';
import colors from '@/theme/colors';
import { BodyMediumMidEmphasisUnderline } from '../Typography';

interface ToastContentProps {
  id: string;
  message: string;
  onClick?: () => void;
  buttonLabel?: string;
  onDismiss?: () => void;
  hideDismiss?: boolean;
}

const restoreFocusToElement = (target: HTMLElement | null): void => {
  if (!target || !document.body.contains(target)) return;
  target.focus({ preventScroll: true });
};

const ToastContent: React.FC<ToastContentProps> = ({
  id,
  message,
  onClick,
  buttonLabel,
  onDismiss,
  hideDismiss = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const didRestoreFocusRef = useRef(false);

  const dismiss = useCallback(() => toast.dismiss(id), [id]);

  const tryRestorePreviousFocus = useCallback((): void => {
    if (didRestoreFocusRef.current) return;
    didRestoreFocusRef.current = true;
    restoreFocusToElement(previousFocusRef.current);
  }, []);

  const { toasts } = useToasterStore();
  const isToastVisible = toasts.find(t => t.id === id)?.visible !== false;

  useLayoutEffect(() => {
    previousFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    didRestoreFocusRef.current = false;

    const attemptFocus = (): void => {
      containerRef.current?.focus({ preventScroll: true });
    };

    attemptFocus();
    const timeoutIds = [
      window.setTimeout(attemptFocus, 0),
      window.setTimeout(attemptFocus, 10),
      window.setTimeout(attemptFocus, 32),
    ];

    return (): void => {
      timeoutIds.forEach(tid => window.clearTimeout(tid));
      tryRestorePreviousFocus();
    };
  }, [tryRestorePreviousFocus]);

  useLayoutEffect(() => {
    if (isToastVisible) return;
    tryRestorePreviousFocus();
    onDismiss?.();
  }, [isToastVisible, tryRestorePreviousFocus, onDismiss]);

  useEffect(() => {
    if (!isToastVisible) return;

    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      dismiss();
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [dismiss, isToastVisible]);

  return (
    <div
      ref={containerRef}
      role="dialog"
      aria-label={message}
      className={styles.container}
      tabIndex={0}
    >
      <span className={styles.message}>{message}</span>
      {onClick && buttonLabel && (
        <AccessibleDivButton
          onClick={() => {
            onClick();
            dismiss();
          }}
        >
          <BodyMediumMidEmphasisUnderline color={colors.shadesJungle500}>
            {buttonLabel}
          </BodyMediumMidEmphasisUnderline>
        </AccessibleDivButton>
      )}
      {!hideDismiss && (
        <IconButton
          onClick={dismiss}
          aria-label="Dismiss Notification"
          data-testid="dismissNotification"
        >
          <CloseIcon className={styles.closeIcon} />
        </IconButton>
      )}
    </div>
  );
};

export const useSnackbar = () => {
  const showToast = ({
    message,
    onClick,
    buttonLabel,
    onDismiss,
    hideDismiss,
  }: {
    message: string;
    onClick?: () => void;
    buttonLabel?: string;
    onDismiss?: () => void;
    hideDismiss?: boolean;
  }) => {
    toast(
      t => (
        <ToastContent
          id={t.id}
          message={message}
          onClick={onClick}
          buttonLabel={buttonLabel}
          onDismiss={onDismiss}
          hideDismiss={hideDismiss}
        />
      ),
      {
        duration: Infinity,
      }
    );
  };

  return showToast;
};

export const SnackbarProvider = () => {
  const { toasts } = useToasterStore();
  const TOAST_LIMIT = 1;

  useEffect(() => {
    toasts
      .filter(t => t.visible)
      .filter((_, i) => i >= TOAST_LIMIT)
      .forEach(t => toast.dismiss(t.id));
  }, [toasts]);

  return (
    <Toaster
      position="top-center"
      reverseOrder={false}
      gutter={0}
      toastOptions={{
        className: styles.toastStyle,
      }}
    />
  );
};
